import React, { useMemo, useState } from 'react';
import { RUBRIC_DIMENSIONS } from '../config/rubricDimensions';
import {
  AVERAGE_COLOR,
  SOFT_CALL_LINE,
  clientColor
} from '../config/chartPalette';
import FilterSelect from './FilterSelect';

/**
 * Every client's rubric profile in ONE plot — a line per client across the ten
 * dimensions, plus the all-client mean.
 *
 * WHY ONE CHART AND NOT SMALL MULTIPLES. This replaced one chart per client.
 * Small multiples are the right call when the question is "what is this client's
 * shape" — you read each panel on its own. They are the wrong call when the
 * question is "who is worst at Pain With a Price Tag", because that means
 * eyeballing the same bar across five separate panels and holding the values in
 * your head. Overlaying makes the comparison a vertical scan at one x position.
 *
 * WHY LINES ACROSS CATEGORIES. Strictly, the x axis is categorical and a line
 * implies continuity between neighbouring dimensions that does not exist. It
 * earns its place anyway: the line is doing the work of grouping ten marks into
 * one readable SHAPE per client, which is the only way five series stay
 * separable at ten x positions. Grouped bars would be fifty bars.
 *
 * THE MEAN IS NOT A SIXTH CLIENT. It is dashed, neutral and heavier — a
 * reference, not a peer. Giving it a categorical hue would say "this is another
 * client", which is the one misreading the line exists to prevent.
 */

export interface ClientProfile {
  tenantId: string;
  name: string;
  dimensions: Record<string, number | null>;
  sessions?: number;
}

interface DimensionProfileChartProps {
  clients: ClientProfile[];
}

// Plot geometry. The viewBox is fixed and the SVG scales to its container, so
// these are design units rather than pixels.
const W = 1000;
const H = 340;
const PAD = { top: 18, right: 18, bottom: 92, left: 44 };
const PLOT_W = W - PAD.left - PAD.right;
const PLOT_H = H - PAD.top - PAD.bottom;
const Y_TICKS = [0, 20, 40, 60, 80, 100];
const MARKER_R = 4.5;

/**
 * Greedily wrap a label to lines of at most `max` characters.
 *
 * SVG <text> does not wrap, so this is done by hand and emitted as <tspan>
 * rows. Staggering alternate labels vertically buys roughly double the
 * horizontal room, which is what makes "Equal Business Stature" fit where
 * "EBS" used to be — the abbreviation was saving space nobody needed.
 */
function wrapLabel(text: string, max = 15): string[] {
  const lines: string[] = [];
  let line = '';
  for (const word of text.split(' ')) {
    const candidate = line ? `${line} ${word}` : word;
    if (candidate.length > max && line) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  return lines;
}

const xAt = (index: number, count: number) =>
  count <= 1 ? PAD.left + PLOT_W / 2 : PAD.left + (index / (count - 1)) * PLOT_W;

const yAt = (score: number) => PAD.top + PLOT_H - (score / 100) * PLOT_H;

/** Dropdown sentinels. Not tenantIds, so they cannot collide with one. */
const ALL_CLIENTS = '__all__';
const CUSTOM = '__custom__';

interface Series {
  key: string;
  label: string;
  color: string;
  isAverage: boolean;
  points: Array<{ x: number; y: number; value: number; dimIndex: number }>;
}

const DimensionProfileChart: React.FC<DimensionProfileChartProps> = ({ clients }) => {
  // Legend doubles as a filter: clicking a client isolates it. Five overlaid
  // lines is at the limit of what stays readable, so the ability to pull one
  // out is what makes ten more clients survivable.
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [hoverDim, setHoverDim] = useState<number | null>(null);

  const dims = RUBRIC_DIMENSIONS;

  const series = useMemo<Series[]>(() => {
    const built: Series[] = clients.map((client, clientIndex) => ({
      key: client.tenantId,
      label: client.name,
      color: clientColor(clientIndex),
      isAverage: false,
      points: dims
        .map((d, i) => {
          const v = client.dimensions?.[d.key];
          return typeof v === 'number'
            ? { x: xAt(i, dims.length), y: yAt(v), value: v, dimIndex: i }
            : null;
        })
        .filter((p): p is Series['points'][number] => p !== null)
    }));

    // Mean across clients that have a value for that dimension. Computed from
    // the same rows the lines draw, so the reference can never disagree with
    // what is on screen.
    const meanPoints = dims
      .map((d, i) => {
        const vals = clients
          .map((c) => c.dimensions?.[d.key])
          .filter((v): v is number => typeof v === 'number');
        if (!vals.length) return null;
        const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
        return { x: xAt(i, dims.length), y: yAt(mean), value: mean, dimIndex: i };
      })
      .filter((p): p is Series['points'][number] => p !== null);

    if (meanPoints.length) {
      built.push({
        key: '__average__',
        label: 'All clients (mean)',
        color: AVERAGE_COLOR,
        isAverage: true,
        points: meanPoints
      });
    }
    return built;
  }, [clients, dims]);

  const visible = series.filter((s) => !hidden.has(s.key));

  // The dropdown and the legend must never disagree, so the dropdown WRITES to
  // the same `hidden` set the legend toggles, and its displayed value is
  // DERIVED back out of that set. One piece of state, two controls.
  const clientKeys = series.filter((s) => !s.isAverage).map((s) => s.key);
  const visibleClients = clientKeys.filter((k) => !hidden.has(k));
  const selection =
    visibleClients.length === clientKeys.length
      ? ALL_CLIENTS
      : visibleClients.length === 1
      ? visibleClients[0]
      : CUSTOM;

  const selectClient = (value: string) => {
    if (value === ALL_CLIENTS) return setHidden(new Set());
    if (value === CUSTOM) return;
    // The mean is never hidden by the dropdown — it is the thing the selected
    // client is being compared against, which is the whole point of narrowing.
    setHidden(new Set(clientKeys.filter((k) => k !== value)));
  };

  const toggle = (key: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  if (!clients.length) {
    return <div className="empty">No scored calls in this range.</div>;
  }

  const path = (s: Series) =>
    s.points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');

  return (
    <div className="profile-chart">
      <FilterSelect
        id="profile-client"
        label="Client"
        value={selection}
        onChange={selectClient}
        options={[
          { id: ALL_CLIENTS, label: `All clients (${clientKeys.length})` },
          ...series
            .filter((x) => !x.isAverage)
            .map((x) => ({ id: x.key, label: x.label })),
          ...(selection === CUSTOM ? [{ id: CUSTOM, label: 'Custom selection' }] : [])
        ]}
        hint={
          selection === ALL_CLIENTS
            ? 'Every client, plus the mean. Pick one to compare it against the mean alone.'
            : 'Shown against the all-client mean.'
        }
      />

      <div className="profile-chart-plot">
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Rubric score by dimension, per client">
          {/* Recessive grid — the data stays the loudest thing on screen. */}
          {Y_TICKS.map((t) => (
            <g key={t}>
              <line
                x1={PAD.left}
                x2={PAD.left + PLOT_W}
                y1={yAt(t)}
                y2={yAt(t)}
                stroke="var(--line-2)"
                strokeWidth={1}
              />
              <text x={PAD.left - 10} y={yAt(t) + 4} textAnchor="end" className="pc-axis">
                {t}
              </text>
            </g>
          ))}

          {/* The rubric's own soft-call line, labelled so it needs no legend. */}
          <line
            x1={PAD.left}
            x2={PAD.left + PLOT_W}
            y1={yAt(SOFT_CALL_LINE)}
            y2={yAt(SOFT_CALL_LINE)}
            stroke="var(--warn)"
            strokeWidth={1}
            strokeDasharray="5 4"
          />
          <text x={PAD.left + PLOT_W} y={yAt(SOFT_CALL_LINE) - 6} textAnchor="end" className="pc-ref">
            soft-call line ({SOFT_CALL_LINE})
          </text>

          {/* Hover crosshair. Full-height hit targets, far bigger than the marks. */}
          {dims.map((d, i) => (
            <rect
              key={d.key}
              x={xAt(i, dims.length) - PLOT_W / (dims.length * 2)}
              y={PAD.top}
              width={PLOT_W / dims.length}
              height={PLOT_H}
              fill="transparent"
              onMouseEnter={() => setHoverDim(i)}
              onMouseLeave={() => setHoverDim(null)}
            />
          ))}
          {hoverDim !== null && (
            <line
              x1={xAt(hoverDim, dims.length)}
              x2={xAt(hoverDim, dims.length)}
              y1={PAD.top}
              y2={PAD.top + PLOT_H}
              stroke="var(--ink-4)"
              strokeWidth={1}
            />
          )}

          {/* Series. The mean draws last so it sits on top of the clients. */}
          {[...visible].sort((a, b) => Number(a.isAverage) - Number(b.isAverage)).map((s) => (
            <g key={s.key}>
              <path
                d={path(s)}
                fill="none"
                stroke={s.color}
                strokeWidth={s.isAverage ? 2.5 : 2}
                strokeDasharray={s.isAverage ? '7 5' : undefined}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              {!s.isAverage &&
                s.points.map((p) => (
                  <circle
                    key={p.dimIndex}
                    cx={p.x}
                    cy={p.y}
                    r={hoverDim === p.dimIndex ? MARKER_R + 1.5 : MARKER_R}
                    fill={s.color}
                    /* 2px surface ring so overlapping marks stay countable. */
                    stroke="var(--surface-2)"
                    strokeWidth={2}
                  />
                ))}
            </g>
          ))}

          {/* X labels: staggered on two bands AND wrapped, so full dimension
              names fit instead of abbreviations nobody can decode. */}
          {dims.map((d, i) => {
            const band = i % 2 === 0 ? 0 : 34;
            return (
              <text
                key={d.key}
                x={xAt(i, dims.length)}
                y={PAD.top + PLOT_H + 18 + band}
                textAnchor="middle"
                className={'pc-xlbl' + (hoverDim === i ? ' on' : '')}
              >
                {wrapLabel(d.chartLabel).map((line, li) => (
                  <tspan key={li} x={xAt(i, dims.length)} dy={li === 0 ? 0 : 11}>
                    {line}
                  </tspan>
                ))}
              </text>
            );
          })}
        </svg>

        {hoverDim !== null && (
          <div className="profile-tip">
            <div className="profile-tip-head">{dims[hoverDim].label}</div>
            {[...visible]
              .map((s) => ({ s, p: s.points.find((pt) => pt.dimIndex === hoverDim) }))
              .filter((r) => r.p)
              .sort((a, b) => (b.p!.value ?? 0) - (a.p!.value ?? 0))
              .map(({ s, p }) => (
                <div className="profile-tip-row" key={s.key}>
                  <span className="swatch" style={{ background: s.color }} />
                  <span className="grow">{s.label}</span>
                  <b>{Math.round(p!.value)}</b>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Legend is always present for >=2 series, and identity is never colour
          alone — every entry carries its name beside the swatch. */}
      <div className="profile-legend">
        {series.map((s) => (
          <button
            key={s.key}
            type="button"
            className={'profile-legend-item' + (hidden.has(s.key) ? ' off' : '')}
            onClick={() => toggle(s.key)}
            title={hidden.has(s.key) ? `Show ${s.label}` : `Hide ${s.label}`}
          >
            <span
              className={'swatch' + (s.isAverage ? ' dashed' : '')}
              style={{ background: s.isAverage ? 'transparent' : s.color, borderColor: s.color }}
            />
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default DimensionProfileChart;
